import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faVolumeUp, faVolumeMute, faTimes, faSliders } from "@fortawesome/free-solid-svg-icons";
import "./components.css";
import "./musicPlayer.css";
import { useAudioWithTime } from "../context/AudioContext";
import AudioControlDrawer from "./AudioControlDrawer";

const MusicPlayer = memo(() => {
  const {
    audioSrc, 
    audioTitle, 
    closePlayer, 
    setAudioElement, 
    currentTime, 
    duration, 
    isPlaying,
    shouldAutoPlay,
    setShouldAutoPlay
  } = useAudioWithTime();

  useEffect(() => {
    console.log('[MusicPlayer] Context isPlaying is NOW:', isPlaying, '(src:', audioSrc, ')');
  }, [isPlaying, audioSrc]);
  
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const volumeBarRef = useRef(null);
  const animationRef = useRef(null);
  const canvasRef = useRef(null);
  const visualizationAnimationRef = useRef(null);
  
  // Shared audio context and nodes for both visualization and EQ
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const dataArrayRef = useRef(null);
  const gainNodeRef = useRef(null);
  const eqFiltersRef = useRef([]);

  // Function to get the shared audio context and nodes
  const getSharedAudioNodes = useCallback(() => {
    return {
      audioContext: audioContextRef.current,
      sourceNode: sourceRef.current,
      analyser: analyserRef.current,
      gainNode: gainNodeRef.current,
      eqFilters: eqFiltersRef.current
    };
  }, []);

  // initializeAudioContext and drawVisualization as previously defined, with their logs.
  const initializeAudioContext = useCallback(() => {
    if (!audioRef.current) {
        console.warn("[MusicPlayer initializeAudioContext] audioRef.current is null. Cannot init Web Audio.");
        return;
    }
    
    try {
      // Only create a new AudioContext if one doesn't exist
      if (!audioContextRef.current) {
        console.log("[MusicPlayer initializeAudioContext] Creating new Web Audio API context for visualization and EQ...");
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        
        // Create analyser for visualization
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 512;
        analyserRef.current.smoothingTimeConstant = 0.8;
        
        // Create gain node for volume control
        gainNodeRef.current = audioContextRef.current.createGain();
        
        // Create EQ filters
        const frequencies = [32, 64, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];
        const filters = frequencies.map((freq) => {
          const filter = audioContextRef.current.createBiquadFilter();
          filter.type = 'peaking';
          filter.frequency.value = freq;
          filter.Q.value = 1;
          filter.gain.value = 0; // Start with no gain
          return filter;
        });
        eqFiltersRef.current = filters;
        
        const bufferLength = analyserRef.current.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);
        console.log("[MusicPlayer initializeAudioContext] Web Audio API context created successfully.");
      }

      // Only create a MediaElementSource if one doesn't exist for this audio element
      if (!sourceRef.current && audioRef.current) {
        console.log("[MusicPlayer initializeAudioContext] Creating MediaElementSource for audio element...");
        sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        
        // Connect the audio graph: source -> EQ filters -> analyser -> gain -> destination
        let currentNode = sourceRef.current;
        
        // Connect through EQ filters
        eqFiltersRef.current.forEach(filter => {
          currentNode.connect(filter);
          currentNode = filter;
        });
        
        // Connect to analyser and gain
        currentNode.connect(analyserRef.current);
        currentNode.connect(gainNodeRef.current);
        gainNodeRef.current.connect(audioContextRef.current.destination);
        
        console.log("[MusicPlayer initializeAudioContext] MediaElementSource connected successfully with EQ chain.");
      }
      
      // Resume audio context if suspended
      if (audioContextRef.current.state === 'suspended') {
        console.log("[MusicPlayer initializeAudioContext] Resuming suspended audio context...");
        audioContextRef.current.resume().then(() => console.log("[MusicPlayer initializeAudioContext] Audio context resumed."));
      }
    } catch (error) {
      console.error("[MusicPlayer initializeAudioContext] Failed to initialize Web Audio API:", error);
      // Don't reset refs if there was an error with MediaElementSource - it might already be connected
      if (error.name !== 'InvalidStateError') {
        audioContextRef.current = null; 
        analyserRef.current = null; 
        sourceRef.current = null; 
        dataArrayRef.current = null;
        gainNodeRef.current = null;
        eqFiltersRef.current = [];
      } else {
        console.warn("[MusicPlayer initializeAudioContext] Audio element already connected, continuing with existing connection...");
      }
    }
  }, []); // Empty dependency array as it operates on refs and window objects.

  const drawVisualization = useCallback(() => {
    // ... (existing drawVisualization logic) ...
    if (!canvasRef.current) {
      // console.log("No canvas ref for drawVisualization");
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (visualizationAnimationRef.current) {
      cancelAnimationFrame(visualizationAnimationRef.current);
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let frequencyData = null;
      if (analyserRef.current && dataArrayRef.current && isPlaying && audioContextRef.current?.state === 'running') {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        frequencyData = dataArrayRef.current;
      }
      const canvasWidth = canvas.width / (window.devicePixelRatio || 1);
      const canvasHeight = canvas.height / (window.devicePixelRatio || 1);
      const gradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      gradient.addColorStop(0, 'rgba(179, 95, 208, 0.6)');
      gradient.addColorStop(0.5, 'rgba(156, 39, 176, 0.4)');
      gradient.addColorStop(1, 'rgba(74, 30, 127, 0.3)');
      const barCount = 128;
      const barWidth = canvasWidth / barCount;
      for (let i = 0; i < barCount; i++) {
        let barHeight;
        if (frequencyData && isPlaying) {
          const dataIndex = Math.floor((i / barCount) * frequencyData.length);
          const amplitude = frequencyData[dataIndex] / 255;
          barHeight = Math.max(10, amplitude * canvasHeight * 0.9);
          if (barHeight < 20) barHeight = 20 + (amplitude * 40);
        } else if (isPlaying) {
          const time = Date.now() * 0.002;
          const baseHeight = (Math.sin(time + i * 0.3) * 0.4 + 0.6) * canvasHeight * 0.6;
          const variation = Math.sin(time * 2 + i * 0.8) * 0.2 * canvasHeight;
          barHeight = Math.max(20, baseHeight + variation);
        } else {
          barHeight = 25 + (i % 3) * 10;
        }
        let barGradient = gradient;
        if (frequencyData && isPlaying) {
          const dataIndex = Math.floor((i / barCount) * frequencyData.length);
          const intensity = frequencyData[dataIndex] / 255;
          barGradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
          barGradient.addColorStop(0, `rgba(${179 + intensity * 76}, ${95 + intensity * 61}, ${208 + intensity * 47}, ${0.4 + intensity * 0.4})`);
          barGradient.addColorStop(0.5, `rgba(${156 + intensity * 99}, ${39 + intensity * 117}, ${176 + intensity * 79}, ${0.3 + intensity * 0.4})`);
          barGradient.addColorStop(1, `rgba(${74 + intensity * 181}, ${30 + intensity * 225}, ${127 + intensity * 128}, ${0.2 + intensity * 0.4})`);
        }
        ctx.fillStyle = barGradient;
        ctx.fillRect(i * barWidth, canvasHeight - barHeight, barWidth - 1, barHeight);
        const glowIntensity = frequencyData && isPlaying ? (frequencyData[Math.floor((i / barCount) * frequencyData.length)] / 255) * 0.6 + 0.2 : 0.3;
        ctx.shadowColor = `rgba(156, 39, 176, ${glowIntensity})`;
        ctx.shadowBlur = 6 + (glowIntensity * 10);
        ctx.fillRect(i * barWidth, canvasHeight - barHeight, barWidth - 1, barHeight);
        ctx.shadowBlur = 0;
      }
      visualizationAnimationRef.current = requestAnimationFrame(draw);
    };
    draw();
  }, [isPlaying]); // drawVisualization depends on isPlaying from context

  useEffect(() => {
    if (canvasRef.current) {
      // console.log("Starting/restarting visualization, isPlaying:", isPlaying);
      drawVisualization();
    }
    return () => {
      if (visualizationAnimationRef.current) {
        cancelAnimationFrame(visualizationAnimationRef.current);
        visualizationAnimationRef.current = null;
      }
    };
  }, [drawVisualization]);

  // Local event listeners for additional debugging (optional)
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const localHandlePlay = () => console.log('[MusicPlayer LOCAL event] "play". Paused:', audio.paused, 'Time:', audio.currentTime);
      const localHandlePause = () => console.log('[MusicPlayer LOCAL event] "pause". Paused:', audio.paused);
      audio.addEventListener("play", localHandlePlay);
      audio.addEventListener("pause", localHandlePause);
      return () => {
        audio.removeEventListener("play", localHandlePlay);
        audio.removeEventListener("pause", localHandlePause);
      };
    }
  }, [audioSrc]); // Re-attach if audioSrc changes (and thus audioRef.current might be new instance or reconfigured)


  // Main effect for handling audio source changes, Web Audio, and linking to AudioProvider
  useEffect(() => {
    const currentAudioElement = audioRef.current;
    let setupComplete = false;

    if (audioSrc && currentAudioElement && setAudioElement) {
      console.log(`[MusicPlayer src-EFFECT] audioSrc is '${audioSrc}'. Setting up element and Web Audio.`);

      // Only reset Web Audio context if the src actually changed (not on re-renders)
      const currentSrc = currentAudioElement.src;
      const isSrcChange = currentSrc !== audioSrc;
      
      if (isSrcChange && audioContextRef.current) {
        console.log("[MusicPlayer src-EFFECT] Source changed, closing existing Web Audio context.");
        audioContextRef.current.close().then(() => console.log("[MusicPlayer src-EFFECT] Old AudioContext closed.")).catch(e => console.warn("Error closing old AudioContext:", e));
        audioContextRef.current = null;
        sourceRef.current = null;
        analyserRef.current = null;
        dataArrayRef.current = null;
        gainNodeRef.current = null;
        eqFiltersRef.current = [];
      }

      // 2. Prepare audio element for the new source only if it changed
      if (isSrcChange) {
        console.log("[MusicPlayer src-EFFECT] Setting new src and calling load() on audio element.");
        currentAudioElement.src = audioSrc;
        currentAudioElement.volume = isMuted ? 0 : volume;
        currentAudioElement.load(); 
      } else {
        console.log("[MusicPlayer src-EFFECT] Source unchanged, skipping load. Setting up element if needed.");
      }
      
      // Add error handling for audio loading
      const handleError = (error) => {
        console.error("[MusicPlayer src-EFFECT] Audio loading error:", error);
        console.error("[MusicPlayer src-EFFECT] Audio element error details:", {
          error: currentAudioElement.error,
          networkState: currentAudioElement.networkState,
          readyState: currentAudioElement.readyState,
          src: currentAudioElement.src
        });
      };

      const handleLoadStart = () => {
        console.log("[MusicPlayer src-EFFECT] Audio load started. NetworkState:", currentAudioElement.networkState);
      };

      const handleLoadedData = () => {
        console.log("[MusicPlayer src-EFFECT] Audio loaded data. ReadyState:", currentAudioElement.readyState, "Duration:", currentAudioElement.duration);
      };

      const handleCanPlay = () => {
        console.log("[MusicPlayer src-EFFECT] Audio can play. ReadyState:", currentAudioElement.readyState);
        if (!setupComplete) {
          setupAudioElement();
        }
      };

      const handleLoadedMetadata = () => {
        console.log("[MusicPlayer src-EFFECT] Audio loaded metadata. Duration:", currentAudioElement.duration);
        if (!setupComplete) {
          setupAudioElement();
        }
      };

      // Add event listeners for debugging
      currentAudioElement.addEventListener('error', handleError);
      currentAudioElement.addEventListener('loadstart', handleLoadStart);
      currentAudioElement.addEventListener('loadeddata', handleLoadedData);
      currentAudioElement.addEventListener('canplay', handleCanPlay);
      currentAudioElement.addEventListener('loadedmetadata', handleLoadedMetadata);

      // 3. Set up the audio element in the context
      const setupAudioElement = () => {
        if (setupComplete) return; // Prevent multiple setups
        setupComplete = true;
        
        console.log("[MusicPlayer src-EFFECT] Setting up audio element in context.");
        initializeAudioContext();
        
        if (setAudioElement) {
            console.log("[MusicPlayer src-EFFECT] Calling setAudioElement (from AudioProvider). Element:", audioRef.current);
            setAudioElement(audioRef.current);
        } else {
            console.warn("[MusicPlayer src-EFFECT] setAudioElement is not available from context!");
        }

        // Auto-play if requested
        if (shouldAutoPlay && setShouldAutoPlay) {
          console.log("[MusicPlayer src-EFFECT] Auto-play requested, attempting to start playback...");
          setShouldAutoPlay(false); // Reset the flag
          
          const attemptAutoPlay = () => {
            console.log("[MusicPlayer src-EFFECT] Attempting auto-play. ReadyState:", currentAudioElement.readyState, "NetworkState:", currentAudioElement.networkState);
            
            if (currentAudioElement.readyState >= 1) { // HAVE_METADATA or higher
              console.log("[MusicPlayer src-EFFECT] Starting auto-play...");
              console.log("[MusicPlayer src-EFFECT] Pre-play audio state - Volume:", currentAudioElement.volume, "Muted:", currentAudioElement.muted, "Duration:", currentAudioElement.duration);
              
              // Ensure volume is set correctly before playing
              currentAudioElement.volume = isMuted ? 0 : volume;
              
              currentAudioElement.play().then(() => {
                console.log("[MusicPlayer src-EFFECT] Auto-play started successfully");
                console.log("[MusicPlayer src-EFFECT] Post-play audio state - Volume:", currentAudioElement.volume, "Muted:", currentAudioElement.muted, "CurrentTime:", currentAudioElement.currentTime, "Paused:", currentAudioElement.paused);
                animationRef.current = requestAnimationFrame(whilePlaying);
              }).catch(error => {
                console.error("[MusicPlayer src-EFFECT] Auto-play failed:", error);
                console.error("[MusicPlayer src-EFFECT] Error details:", {
                  name: error.name,
                  message: error.message,
                  code: error.code
                });
                // Try manual interaction prompt
                console.warn("[MusicPlayer src-EFFECT] Auto-play blocked by browser. User interaction required.");
              });
            } else {
              console.warn("[MusicPlayer src-EFFECT] Audio not ready for auto-play. ReadyState:", currentAudioElement.readyState);
              // Try again after a short delay
              setTimeout(attemptAutoPlay, 200);
            }
          };
          
          // Try auto-play after a short delay
          setTimeout(attemptAutoPlay, 150);
        }
      };

      // Try to set up immediately if the element is ready
      if (currentAudioElement.readyState >= 1) { // HAVE_METADATA or higher
        console.log("[MusicPlayer src-EFFECT] Audio already ready, setting up immediately.");
        setupAudioElement();
      } else {
        console.log("[MusicPlayer src-EFFECT] Audio not ready, waiting for events...");
      }

      // Cleanup function for this specific setup
      return () => {
        console.log(`[MusicPlayer src-EFFECT Cleanup] Cleaning up for src: ${audioSrc}`);
        currentAudioElement.removeEventListener('error', handleError);
        currentAudioElement.removeEventListener('loadstart', handleLoadStart);
        currentAudioElement.removeEventListener('loadeddata', handleLoadedData);
        currentAudioElement.removeEventListener('canplay', handleCanPlay);
        currentAudioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };

    } else if (!audioSrc && setAudioElement) {
      console.log("[MusicPlayer src-EFFECT] audioSrc is null. Calling setAudioElement(null) to detach from provider.");
      setAudioElement(null);

      if (audioContextRef.current) {
        console.log("[MusicPlayer src-EFFECT] audioSrc is null, ensuring local Web Audio context is closed.");
        audioContextRef.current.close().catch(e => console.warn("Error closing context in src-EFFECT (audioSrc is null):", e));
        audioContextRef.current = null; 
        sourceRef.current = null; 
        analyserRef.current = null; 
        dataArrayRef.current = null;
        gainNodeRef.current = null;
        eqFiltersRef.current = [];
      }
    }
  }, [audioSrc, setAudioElement, initializeAudioContext, shouldAutoPlay, setShouldAutoPlay]); // Removed volume and isMuted to prevent re-runs

  // Separate effect to handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
    // Also update the Web Audio gain node if it exists
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlayPause = () => {
    if (!audioRef.current) {
        console.warn("[MusicPlayer togglePlayPause] No audio element reference.");
        return;
    }
    
    const actuallyPaused = audioRef.current.paused;
    console.log(`[MusicPlayer togglePlayPause] Action. Context isPlaying: ${isPlaying}. AudioEl Paused: ${actuallyPaused}. ReadyState: ${audioRef.current.readyState}. Src: ${audioRef.current.src}`);
    console.log(`[MusicPlayer togglePlayPause] Audio element volume: ${audioRef.current.volume}. Muted: ${audioRef.current.muted}. Duration: ${audioRef.current.duration}. CurrentTime: ${audioRef.current.currentTime}`);
    
    // Use the actual audio element state as the source of truth for toggle decision
    if (actuallyPaused) {
      console.log("[MusicPlayer togglePlayPause] Audio is paused, attempting to PLAY.");
      
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        console.log("[MusicPlayer togglePlayPause] Resuming suspended Web Audio Context.");
        audioContextRef.current.resume().catch(e => console.warn("Error resuming audio context:", e));
      } else if (!audioContextRef.current && audioRef.current.src && audioRef.current.readyState >= 2) {
        console.log("[MusicPlayer togglePlayPause] Web Audio Context not found or not ready, attempting to initialize.");
        initializeAudioContext();
      }
      
      audioRef.current.play().then(() => {
        console.log("[MusicPlayer togglePlayPause] audio.play() Promise RESOLVED. Element now paused:", audioRef.current.paused);
        console.log("[MusicPlayer togglePlayPause] After play - Volume:", audioRef.current.volume, "Muted:", audioRef.current.muted, "CurrentTime:", audioRef.current.currentTime);
        animationRef.current = requestAnimationFrame(whilePlaying);
      }).catch(error => {
        console.error("[MusicPlayer togglePlayPause] audio.play() Promise REJECTED:", error);
      });
    } else {
      console.log("[MusicPlayer togglePlayPause] Audio is playing, attempting to PAUSE.");
      audioRef.current.pause();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  const whilePlaying = () => {
    if (audioRef.current && progressBarRef.current && !audioRef.current.paused) {
      animationRef.current = requestAnimationFrame(whilePlaying);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  const changeRange = () => {
    if (!audioRef.current || !progressBarRef.current) return;
    const newTime = parseFloat(progressBarRef.current.value);
    console.log("[MusicPlayer changeRange] Seeking to:", newTime);
    audioRef.current.currentTime = newTime;
  };

  const changeVolume = () => {
    if (!audioRef.current || !volumeBarRef.current) return;
    const newVolume = parseFloat(volumeBarRef.current.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    if (newMuteState) {
      audioRef.current.volume = 0;
      if (volumeBarRef.current) volumeBarRef.current.value = 0;
    } else {
      audioRef.current.volume = volume;
      if (volumeBarRef.current) volumeBarRef.current.value = volume;
    }
  };

  const handleClose = () => {
    console.log("[MusicPlayer handleClose] Initiating close sequence.");
    if (audioRef.current) {
      audioRef.current.pause(); 
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    }
    if (visualizationAnimationRef.current) cancelAnimationFrame(visualizationAnimationRef.current);
    
    if (audioContextRef.current) {
        console.log("[MusicPlayer handleClose] Explicitly closing Web Audio API context.");
        audioContextRef.current.close().catch(e => console.warn("Error closing Web Audio in handleClose:", e));
        audioContextRef.current = null; 
        analyserRef.current = null; 
        sourceRef.current = null; 
        dataArrayRef.current = null;
        gainNodeRef.current = null;
        eqFiltersRef.current = [];
    }
    
    closePlayer();
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === Infinity) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
  };

  if (!audioSrc) {
    return null;
  }

  return (
    <>
      <canvas 
        ref={canvasRef}
        style={{
          position: 'fixed',
          bottom: '0px',
          left: '0px',
          width: '100vw',
          height: '200px',
          zIndex: 998,
          pointerEvents: 'none',
          opacity: 0.3,
          background: 'linear-gradient(to top, rgba(26, 9, 51, 0.1) 0%, rgba(74, 30, 127, 0.05) 50%, transparent 100%)',
          margin: 0,
          padding: 0,
          border: 'none',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
      <div className="music-player-wrapper">
        <Container className={`music-player beats-card ${isPlaying ? 'active' : ''}`}>
          <audio 
            ref={audioRef} 
            preload="metadata"
            crossOrigin="anonymous"
          />
          <Row className="align-items-center">
            <Col xs={2} md={1} className="text-center">
              <button 
                className="beats-btn-purple-ctrl rounded-circle p-2" 
                onClick={togglePlayPause}
                title={isPlaying ? "Pause" : "Play"}
              > 
                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
              </button>
            </Col>
            <Col xs={6} md={3} className="song-title">
              <div className="beats-gradient">{audioTitle || "No track selected"}</div>
            </Col>
            <Col xs={12} md={4} className="mt-2 mt-md-0">
              <div className="d-flex align-items-center">
                <span className="time-display me-2">{formatTime(currentTime)}</span>
                <input
                  type="range"
                  ref={progressBarRef}
                  className="progress-bar w-100"
                  value={currentTime}
                  onChange={changeRange}
                  min="0"
                  max={duration || 0}
                  step="0.01"
                  disabled={!audioSrc || duration === 0}
                />
                <span className="time-display ms-2">{formatTime(duration)}</span>
              </div>
            </Col>
            <Col xs={10} md={2} className="volume-controls mt-2 mt-md-0">
              <div className="d-flex align-items-center">
                <button 
                  className="volume-btn me-2" 
                  onClick={toggleMute}
                  title={isMuted || volume === 0 ? "Unmute" : "Mute"}
                >
                  <FontAwesomeIcon icon={isMuted || volume === 0 ? faVolumeMute : faVolumeUp} />
                </button>
                <input
                  type="range"
                  ref={volumeBarRef}
                  className="volume-bar"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={changeVolume}
                  disabled={!audioSrc}
                />
              </div>
            </Col>
            <Col xs={2} md={1} className="text-center d-flex">
              <span
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  margin: '0',
                  boxSizing: 'border-box',
                  color: "purple",
                  fontWeight: "bold",
                  boxShadow: "0 0 10px 0 rgba(123, 28, 139, 0.6)",
                  borderRadius: "5px",
                  cursor: "pointer"
                }}
                className={`p-2 me-1 ${isDrawerOpen ? 'active' : ''}`}
                onClick={toggleDrawer}
                title="Audio Controls"
              >
                EQ
              </span>
              <button 
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  margin: '0',
                  boxSizing: 'border-box',
                  color: '#d9d9d9'
                }}
                className="p-2" 
                onClick={handleClose}
                title="Close Player"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Audio Control Drawer */}
      <AudioControlDrawer 
        audioElement={audioRef.current}
        isOpen={isDrawerOpen}
        onToggle={toggleDrawer}
        getSharedAudioNodes={getSharedAudioNodes}
      />
    </>
  );
});

export default MusicPlayer; 